import { useEffect, useState } from 'react';

/**
 * Client-side case-insensitive substring filter over `usePeople()` results,
 * shared by `PersonValuePicker` (single-select) and `PersonMultiValuePicker`
 * (multi-select) — both dropdowns filter the same "Everyone else"/candidate
 * list by typed search text, so the filter itself lives in one place (Reuse
 * Before You Build) rather than being reimplemented per component.
 */
export function filterPeopleByName<T extends { name: string }>(people: T[], search: string): T[] {
  const q = search.trim().toLowerCase();
  if (!q) return people;
  return people.filter(p => p.name.toLowerCase().includes(q));
}

/**
 * Search-text state that resets to `''` whenever `open` transitions
 * false→true — reopening a person-picker dropdown always starts from a
 * fresh, unfiltered list rather than carrying over the previous search.
 * A plain effect-on-`open` reset is sufficient here (not a StrictMode
 * lifetime-tracking ref): this is UI-visible search text, not state that
 * needs to survive a dev-mode double-invoke, and resetting it twice on a
 * double-mount is a harmless no-op.
 */
export function usePersonSearch(open: boolean) {
  const [search, setSearch] = useState('');
  useEffect(() => {
    if (open) setSearch('');
  }, [open]);
  return [search, setSearch] as const;
}
